<template>
  <base-collapsible-block :isCollapsed="true"
                          class="collapsible-block"
                          ref="collapsibleBlock"
                          title="Advanced Options"
                          @collapsed="advancedCollapsed">
    <advanced-chips-block
      :copyPage="copyPage"
      slot="header-additional"
      :options="appliedSelectedOptions"
      @copy="$emit('copy')"
      :replicable="replicable"
      :forceCalculate="forceCalculate"
      @delete-option="deleteChip"/>

    <div class="common-table">
      <div class="common-table__row common-table__header advanced-header">
        <div class="common-table__header-cell advanced-header__field">
          <div class="advanced-header__cell">
            Field
          </div>
        </div>
        <div class="common-table__header-cell advanced-header__divider advanced-header__type">
          <div class="advanced-header__cell ">
            Operator
          </div>
        </div>
        <div
          class="common-table__header-cell advanced-header__filter advanced-header__divider">
          <div class="advanced-header__cell">
            Filter Value
          </div>
          <div class="advanced-header__button">
            <base-material-design-icon icon="plusCircle"
                                       :iconStyle="{width: '20px', height: '20px', color: '#284aff'}"
                                       @click="addNew()"
            />
            <span class="advanced-header__button_label">Add New</span>
          </div>
        </div>

      </div>
      <div class="advanced-no-data" v-if="selectedOptions.length === 0">
        No filter selected
      </div>
      <div class="" v-else>
        <advanced-options-table-row v-for="(item) in selectedOptions"
                                    :key="item.id"
                                    :options="options"
                                    :item="item"
                                    :is-validating="isValidating"
                                    :is-first="item.isFirst"
                                    @delete="deleteItem(item)"
                                    @update-item="updateItem($event, item)"
                                    />
      </div>
      <div class="advanced-controls">
        <button class="advanced-button advanced-button_clear"
                :class="{'advanced-button_disabled': resetButtonDisabled}"
                @click.prevent="clear()">
          Clear All
        </button>
        <button class="advanced-button advanced-button_apply"
                @click.prevent="apply()">
          Apply
        </button>
      </div>
    </div>
  </base-collapsible-block>
</template>
<script>
import _ from 'lodash'
import AdvancedOptionsTableRow from './AdvancedOptionsTableRow'
import AdvancedChipsBlock from './AdvancedChipsBlock'
import { mapActions } from 'vuex'
import Config from './config'
/**
 * Advanced Options component. Used in RankZone components and Content Brief Reporting tab
 */
export default {
  name: 'AdvancedOptions',

  components: {
    AdvancedOptionsTableRow,
    AdvancedChipsBlock
  },
  props: {
    /**
     * options received form {@link Config} file and filled in parent component
     */
    options: {
      type: Array,
      default: () => []
    },
    // predefined advanced options received from url query
    predefinedOptions: {
      type: String,
      default: null
    },
    // if false this advanced options component does not support copy/paste functionality
    replicable: {
      type: Boolean,
      default: true
    },
    /**
     * if changed from false to true in {@link AdvancedChipsBlock} should be recalculated chips with
     * it's hack for Reporting tab where loading data flow is different
     */
    forceCalculate: {
      type: Boolean,
      default: false
    },
    // allow copy filter logic without selected filters (copy page link with client id)
    copyPage: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      // options selected by user
      selectedOptions: [],
      // options applied by user
      appliedSelectedOptions: [],
      // true if options were applied false if all AO were cleared or after first load page
      isFiltersApplied: false,
      // if true - start validating logic for children. It happens in case when at least one value is invalid
      isValidating: false,
      isAdvancedCollapsed: true,
      // true if there is only one clear row in the list
      // we need it because in empty list should be one empty string
      isPristine: true,
      emptyOption: {
        selectedField: '',
        selectedOperator: '',
        selectedFilter: {}
      }
    }
  },
  watch: {
    options: {
      handler: 'handlePredefinedFilters',
      immediate: true
    },
    clientId (v, prevValue) {
      if (prevValue) {
        if (!this.isAdvancedCollapsed) {
          this.$refs.collapsibleBlock.toggleCollapsedStatus()
        }
        this.clear()
      }
    },
    '$route.name' () {
      if (!this.isAdvancedCollapsed) {
        this.$refs.collapsibleBlock.toggleCollapsedStatus()
      }
    }
  },
  computed: {
    resetButtonDisabled () {
      return this.selectedOptions.length === 1 && this.isPristine
    },

    isAllFieldsValid () {
      return this.selectedOptions.every(item => {
        const fieldValid = !_.isEmpty(item.selectedField)
        const filterValue = _.get(item, 'selectedFilter.value')
        const filterValid = Boolean(filterValue) || parseInt(filterValue) === 0
        const operatorValid = !_.isEmpty(item.selectedOperator)
        return fieldValid && filterValid && operatorValid
      })
    }
  },
  methods: {
    ...mapActions('rankZoneCommon', ['resetAutocomplete']),
    /**
     * callback for options props
     * */
    handlePredefinedFilters () {
      // if there are predefined AO received from URL query - we should restore all component data
      if (this.predefinedOptions) {
        const parsedFilters = JSON.parse(this.predefinedOptions)
        const mappedFilters = parsedFilters.filters.map((item, index) => {
          const option = this.options.find(opt => opt.type === item.selectedField)
          const isFirst = index === 0
          const id = Date.now().valueOf() + index // to avoid coincidence of ids
          return {
            ...option, ...item, isFirst, id
          }
        })
        this.appliedSelectedOptions = mappedFilters
        this.selectedOptions = _.cloneDeep(this.appliedSelectedOptions)
        this.isPristine = false
        this.isFiltersApplied = true
        // we should not emit 'apply-advanced' here because it should be done in parent component
      }
    },
    /**
     * add new row in the table
     * */
    addNew () {
      this.setValidating(false)
      const options = _.cloneDeep(this.emptyOption)
      options.isFirst = (this.selectedOptions.length === 0)
      this.selectedOptions = [...this.selectedOptions, { id: Date.now().valueOf(), ...options }]
    },
    /**
     * clear all data in  the table and emit empty AO data to reload non-filtered parent data
     * */
    clear () {
      this.setValidating(false)
      this.isPristine = true
      this.selectedOptions = []
      this.appliedSelectedOptions = []
      if (this.isFiltersApplied) {
        this.isFiltersApplied = false
        this.$emit('apply-advanced', [])
      }
      this.addNew()
    },
    /**
     * delete item in the table
     * */
    deleteItem (item) {
      const itemIndex = this.selectedOptions.findIndex(option => option.id === item.id)
      if (itemIndex === -1) {
        return
      }
      this.selectedOptions.splice(itemIndex, 1)
      if (this.selectedOptions.length === 0) {
        // this.isPristine = true
        this.apply()
        this.addNew()
      }
    },
    /**
     * delete chip i the {@link AdvancedChipsBlock}
     * after deleting chip parent data should be updated
     * */
    deleteChip (option) {
      const index = this.appliedSelectedOptions.findIndex(item => item.id === option.id)
      this.appliedSelectedOptions.splice(index, 1)
      if (this.selectedOptions.length) {
        const deletingOption = this.selectedOptions.find(item => option.id === item.id)
        if (deletingOption) {
          this.selectedOptions.splice(this.selectedOptions.indexOf(deletingOption), 1)
        }
      }
      this.$emit('apply-advanced', this.appliedSelectedOptions)
    },
    /**
     * change item from {@link AdvancedOptionsTableRow}
     * */
    updateItem (value, item) {
      this.isPristine = false
      Object.assign(item, value)
    },
    /**
     * apply button callback
     * */
    apply () {
      if (this.isPristine) {
        // if there was earlier applied data - we need to clear it and
        // notify parent component about necessity to reload data without AO filtering
        if (this.appliedSelectedOptions.length > 0) {
          this.clear()
          return
        }
        // there we do nothing
        if (this.selectedOptions.length === 1) {
          return
        }
      }
      // if there is AO data and data is valid - notify parent about reload filtered data
      if (this.isAllFieldsValid) {
        this.appliedSelectedOptions = _.cloneDeep(this.selectedOptions)
        this.setValidating(false)
        this.isFiltersApplied = true
        this.$emit('apply-advanced', this.appliedSelectedOptions)
      } else {
        this.setValidating(true)
      }
    },
    /**
     * callback invoked when AO block collapsed/revealed
     * */
    advancedCollapsed (collapsed) {
      this.isAdvancedCollapsed = collapsed
      if (collapsed) {
        // by collapse list of selected options should be cleared
        this.selectedOptions = []
        this.resetAutocomplete()
        return
      }
      // by reveal block we need or restore AO data or restore start state
      if (this.appliedSelectedOptions.length > 0) {
        this.resetAppliedOptions()
      } else {
        this.isPristine = true
        this.addNew()
      }
    },
    /**
     * restore AO after expand component
     * we need it because for hide/show AO component should be restored last applied AO data
     */
    resetAppliedOptions () {
      this.selectedOptions = _.cloneDeep(this.appliedSelectedOptions)
    },
    setValidating (validating) {
      this.isValidating = validating
    }
  }
}
</script>

<style scoped lang="scss">
  .common-table {
    overflow: visible;

    &__header-cell {
      padding: 20px 0;

      &:first-child {
      }

      &:last-child {
        padding-right: 60px;
      }
    }
  }

  .collapsible-block {
    &::v-deep .collapsible__content {
      overflow: visible;
    }
  }

  .advanced-header {
    background-color: rgba(1, 22, 39, 0.1);

    &__cell {
      padding-left: 30px;
      color: $rfx-dark-march;
    }

    &__field {
      flex: 1 1 20%;
    }

    &__type {
      flex: 1 0 15%;
      min-width: 170px;
    }

    &__filter {
      flex: 3 1 65%;
      position: relative;
    }

    &__button {
      position: absolute;
      top: 18px;
      right: 30px;
      color: $rfx-dark-march;
      display: flex;
      align-items: center;
      cursor: pointer;

      &_label {
        font-size: 12px;
        font-weight: 500;
        color: #011627;
        margin-left: 12px;
        white-space: nowrap;
        margin-left: 12px;
      }
    }

    &__divider {
      border-left: 1px solid #fff;
    }
  }

  .advanced-no-data {
    font-size: 22px;
    text-transform: uppercase;
    color: $rfx-pale-grey;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 30px;
  }
  .advanced-controls {
    display: flex;
    justify-content: flex-end;
    padding: 20px 30px;
  }

  .advanced-button {
    height: 40px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    margin-left: 30px;
    border: none;
    outline: none;
    cursor: pointer;

    &_apply {
      background-color: $rfx-medium-march;
      color: #fff;
      width: 100px;
    }

    &_disabled {
      color: $rfx-neutral-grey !important;
      pointer-events: none;
      background-color: transparent;
      cursor: default;
    }

    &_clear {
      background-color: transparent;
      color: $rfx-dark-march;
      width: 100px;
    }
  }
</style>
